import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toISODate } from "./formatters";

type Preset = "all" | "ytd" | "mtd" | "custom";

function computePresetDates(preset: Preset): { from: string; to: string } {
  const today = toISODate(new Date());
  const year = new Date().getFullYear();
  const month = new Date().getMonth();
  switch (preset) {
    case "all":
      return { from: "2024-01-01", to: today };
    case "ytd":
      return { from: `${year}-01-01`, to: today };
    case "mtd":
      return { from: toISODate(new Date(year, month, 1)), to: today };
    case "custom":
      return { from: toISODate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)), to: today };
  }
}

const presets: { key: Preset; label: string }[] = [
  { key: "all", label: "All tid" },
  { key: "ytd", label: "I år" },
  { key: "mtd", label: "Denne mnd" },
  { key: "custom", label: "Egendefinert" },
];

export function PeriodSelector({ periodFrom, setPeriodFrom, periodTo, setPeriodTo }: {
  periodFrom: string;
  setPeriodFrom: (v: string) => void;
  periodTo: string;
  setPeriodTo: (v: string) => void;
}) {
  const [activePreset, setActivePreset] = useState<Preset>("mtd");

  const handlePreset = (preset: Preset) => {
    setActivePreset(preset);
    if (preset !== "custom") {
      const { from, to } = computePresetDates(preset);
      setPeriodFrom(from);
      setPeriodTo(to);
    }
  };

  const handleCustomDate = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setActivePreset("custom");
    setter(e.target.value);
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-1 rounded-lg bg-admin-card p-1">
        {presets.map((p) => (
          <Button
            key={p.key}
            variant={activePreset === p.key ? "default" : "ghost"}
            size="sm"
            onClick={() => handlePreset(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {activePreset === "custom" && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={periodFrom}
            onChange={handleCustomDate(setPeriodFrom)}
            className="rounded-md border border-admin-card-border bg-admin-card px-2 py-1 text-sm text-admin-text-primary"
          />
          <span className="text-sm text-admin-text-subtle">–</span>
          <input
            type="date"
            value={periodTo}
            onChange={handleCustomDate(setPeriodTo)}
            className="rounded-md border border-admin-card-border bg-admin-card px-2 py-1 text-sm text-admin-text-primary"
          />
        </div>
      )}
    </div>
  );
}
